/**
 * Guards the one option a caller can silently get wrong, at construction rather than weeks later on a
 * dashboard. At or below 0 the draw never passes, so the subject emits nothing and looks idle; above 1 the
 * payload advertises a rate consumers scale by, understating counts.
 */
export function assertValidDiagnosticSampleRate(subject: string, sampleRate: number | undefined): void {
    if (sampleRate === undefined) return;
    if (!isValidSampleRateForWeight(sampleRate)) {
        throw new Error(
            `Invalid sampleRate ${sampleRate} for diagnostics on "${subject}": expected a number in (0, 1].`,
        );
    }
}

/**
 * Whether a diagnostics line survives sampling. Every outcome is sampled at the same rate — failures are
 * not exempted — so the lines are an unbiased sample and outcome ratios hold at any rate.
 */
export function shouldEmitDiagnostics(sampleRate: number): boolean {
    return sampleRate >= 1 || Math.random() < sampleRate;
}

/**
 * A line's contribution to a sampling-corrected count. Summing this beats counting lines: no consumer-side
 * scaling. Degrades to 1 on an invalid rate rather than emitting the JSON null that would poison the sum.
 */
export function sampleWeightFor(sampleRate: number): number {
    return isValidSampleRateForWeight(sampleRate) ? 1 / sampleRate : 1;
}

/**
 * The one definition of a usable sample rate: `(0, 1]`. A rate above 1 claims we sampled more than
 * everything, and its weight below 1 would silently understate every corrected count.
 *
 * Both the construction-time assert and {@link sampleWeightFor} route through this, so the rate a
 * wrapper accepts and the rate that produces a real weight cannot drift apart.
 */
export function isValidSampleRateForWeight(sampleRate: number): boolean {
    return Number.isFinite(sampleRate) && sampleRate > 0 && sampleRate <= 1;
}
